import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api';

// mode: 'normal' | 'anon-sender' | 'anon-receiver'
export default function ConversationPage({ mode }) {
  const { userId, relatedUserId } = useParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [otherName, setOtherName] = useState('');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const bottomRef = useRef(null);

  const targetId = mode === 'normal' ? userId : relatedUserId;

  useEffect(() => { load(); }, [targetId, mode]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  async function load() {
    setLoading(true);
    setError('');
    try {
      if (mode === 'normal') {
        const data = await api.getConversationWith(targetId);
        setMessages(data);
        const usersData = await api.listLetters(new Date().getFullYear());
        const u = usersData.find(l => String(l.user_id) === String(targetId));
        setOtherName(u?.display_name || 'Usuario');
      } else {
        const data = await api.getAnonConversation(targetId);
        setMessages(data.messages);
        setOtherName(data.header_name);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleSend() {
    if (!content.trim()) return;
    setSending(true);
    setError('');
    try {
      if (mode === 'normal') {
        await api.sendMessage(parseInt(targetId), content.trim());
      } else if (mode === 'anon-sender') {
        await api.sendToSecretSanta(parseInt(targetId), content.trim());
      } else {
        await api.replyAsSecretSanta(parseInt(targetId), content.trim());
      }
      setContent('');
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  }

  const headerTitle = otherName || '...';

  return (
    <div style={styles.page}>
      <button className="btn btn-ghost" style={{ marginBottom: '0.75rem' }} onClick={() => navigate('/mensajes')}>← Mensajes</button>

      <h3 style={styles.title}>{headerTitle}</h3>
      {mode === 'anon-receiver' && (
        <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginBottom: '0.75rem' }}>
          🎭 Tu identidad se mantiene oculta en esta conversación.
        </p>
      )}

      {loading ? (
        <p style={{ textAlign: 'center', color: 'var(--text-muted)' }}>Cargando...</p>
      ) : (
        <div style={styles.thread}>
          {messages.length === 0 && (
            <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '1rem' }}>
              Todavía no hay mensajes. ¡Escribe el primero!
            </p>
          )}
          {messages.map(m => (
            <div key={m.id} style={{ ...styles.bubbleWrap, justifyContent: m.mine ? 'flex-end' : 'flex-start' }}>
              <div style={{ ...styles.bubble, ...(m.mine ? styles.bubbleMine : styles.bubbleTheirs) }}>
                <p style={styles.bubbleText}>{m.content}</p>
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
      )}

      {error && <p className="error-msg" style={{ textAlign: 'center' }}>⚠ {error}</p>}

      <div style={styles.inputRow}>
        <textarea
          className="input"
          rows={1}
          style={{ flex: 1, resize: 'none' }}
          value={content}
          onChange={e => setContent(e.target.value)}
          placeholder="Escribe un mensaje..."
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
        />
        <button className="btn btn-primary" onClick={handleSend} disabled={sending || !content.trim()}>
          ➤
        </button>
      </div>
    </div>
  );
}

const styles = {
  page: { padding: '1rem', display: 'flex', flexDirection: 'column', minHeight: '85vh' },
  title: { color: 'var(--gold)', fontFamily: 'var(--font-display)', fontSize: '1.15rem' },
  thread: { flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.5rem', marginBottom: '1rem' },
  bubbleWrap: { display: 'flex' },
  bubble: { maxWidth: '75%', padding: '0.6rem 0.9rem', borderRadius: '14px' },
  bubbleMine: { background: 'var(--gold)', color: '#1a0f35', borderBottomRightRadius: '4px' },
  bubbleTheirs: { background: 'var(--bg-card2)', color: 'var(--text)', borderBottomLeftRadius: '4px' },
  bubbleText: { fontSize: '0.92rem', lineHeight: 1.4, whiteSpace: 'pre-wrap' },
  inputRow: { display: 'flex', gap: '0.5rem', alignItems: 'center' },
};
